import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, query } from '../lib/api';
import { useAsync } from '../lib/use-async';
import {
  ApiModule,
  Application,
  ConsentSummary,
  EffectivePermission,
  GatewayUser,
  GrantRule,
  ResourceEntry,
  Role,
  UserRole,
} from '../lib/types';
import { formatDate, formatDateTime } from '../lib/format';
import { Badge, Card, Empty, Field, Modal, Notice, PageHead } from '../components/ui';
import { PermissionMatrix } from '../components/PermissionMatrix';

interface UserDetailData {
  user: GatewayUser;
  roles: UserRole[];
  consents: Array<ConsentSummary & { module_id: string; application_name: string; rules: GrantRule[] }>;
}

export function UserDetail() {
  const { id = '' } = useParams();
  const detail = useAsync(() => api.get<UserDetailData>(`/api/admin/users/${id}`), [id]);
  const modules = useAsync(() => api.get<ApiModule[]>('/api/admin/modules'));
  const roles = useAsync(() => api.get<Role[]>('/api/admin/roles'));
  const [assigning, setAssigning] = useState(false);
  const [moduleId, setModuleId] = useState('');

  const chosen = moduleId || modules.data?.[0]?.id || '';

  if (detail.error) return <Notice tone="danger">{detail.error}</Notice>;
  if (!detail.data) return <p className="muted">Loading...</p>;

  const { user } = detail.data;

  async function toggleAdmin(): Promise<void> {
    await api.patch(`/api/admin/users/${id}/admin`, { isAdmin: !user.is_admin });
    detail.reload();
  }

  async function unassign(roleId: string): Promise<void> {
    await api.delete(`/api/admin/users/${id}/roles/${roleId}`);
    detail.reload();
  }

  async function revokeConsent(consentId: string): Promise<void> {
    if (!window.confirm('Revoke this consent? The application immediately loses access in the name of this person.'))
      return;
    await api.delete(`/api/admin/users/${id}/consents/${consentId}`);
    detail.reload();
  }

  return (
    <>
      <PageHead
        title={user.display_name ?? user.email ?? 'User'}
        description={user.email ?? undefined}
        actions={
          <button onClick={() => void toggleAdmin()}>
            {user.is_admin ? 'Revoke administrator rights' : 'Make administrator'}
          </button>
        }
      />

      <Card
        title="Roles"
        hint="derived from groups or assigned by hand"
        actions={
          <button className="primary small" onClick={() => setAssigning(true)}>
            Assign role
          </button>
        }
        tight
      >
        {detail.data.roles.length ? (
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Origin</th>
                <th>Expires</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {detail.data.roles.map((role) => (
                <tr key={role.id}>
                  <td>
                    {role.role_name}
                    {role.application_name ? (
                      <span className="muted tiny"> · through {role.application_name}</span>
                    ) : null}
                  </td>
                  <td>
                    {role.source === 'group' ? (
                      <Badge>Entra group</Badge>
                    ) : (
                      <Badge tone="accent">by hand</Badge>
                    )}
                  </td>
                  <td>{role.expires_at ? formatDate(role.expires_at) : <span className="muted">unlimited</span>}</td>
                  <td>
                    {role.source === 'manual' ? (
                      <button className="small danger" onClick={() => void unassign(role.role_id)}>
                        Remove
                      </button>
                    ) : (
                      <span className="muted tiny">
                        via the group membership
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>This person has no role - and therefore no permissions.</Empty>
        )}
      </Card>

      <UserExceptions
        userId={id}
        moduleId={chosen}
        modules={modules.data ?? []}
        onModuleChange={setModuleId}
      />

      <Card title="Granted consents" hint="What this person has allowed applications to do in their name" tight>
        {detail.data.consents.length ? (
          <table>
            <thead>
              <tr>
                <th>Application</th>
                <th>Module</th>
                <th>Scope</th>
                <th>Granted</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {detail.data.consents.map((consent) => (
                <tr key={consent.id}>
                  <td>{consent.application_name}</td>
                  <td className="mono">{consent.module_key}</td>
                  <td className="mono muted tiny">
                    {consent.rules.map((rule) => `${rule.resource}.${rule.action}`).join(', ') || '—'}
                  </td>
                  <td className="nowrap">{formatDateTime(consent.granted_at)}</td>
                  <td>
                    {consent.state === 'active' ? (
                      <button className="small danger" onClick={() => void revokeConsent(consent.id)}>
                        Revoke
                      </button>
                    ) : (
                      <Badge>revoked</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No consents granted.</Empty>
        )}
      </Card>

      {assigning ? (
        <AssignRoleDialog
          userId={id}
          roles={roles.data ?? []}
          onClose={() => setAssigning(false)}
          onSaved={() => {
            setAssigning(false);
            detail.reload();
          }}
        />
      ) : null}
    </>
  );
}

function UserExceptions({
  userId,
  moduleId,
  modules,
  onModuleChange,
}: {
  userId: string;
  moduleId: string;
  modules: ApiModule[];
  onModuleChange: (id: string) => void;
}) {
  const resources = useAsync(
    () => (moduleId ? api.get<ResourceEntry[]>(`/api/admin/modules/${moduleId}/resources`) : Promise.resolve([])),
    [moduleId],
  );
  const stored = useAsync(
    () =>
      moduleId
        ? api.get<GrantRule[]>(`/api/admin/users/${userId}/modules/${moduleId}/exceptions`)
        : Promise.resolve([]),
    [moduleId, userId],
  );
  const applications = useAsync(() => api.get<Application[]>('/api/applications'));
  // Without an application only global roles count. With one, the view is
  // what this person gets through it: its grants and its roles come in.
  const [applicationId, setApplicationId] = useState('');
  const effective = useAsync(
    () =>
      moduleId
        ? api.get<EffectivePermission[]>(
            `/api/admin/users/${userId}/modules/${moduleId}/effective${query({ applicationId })}`,
          )
        : Promise.resolve([]),
    [moduleId, userId, applicationId],
  );

  const [draft, setDraft] = useState<GrantRule[] | null>(null);
  const rules = draft ?? stored.data ?? [];

  async function save(): Promise<void> {
    await api.put(`/api/admin/users/${userId}/modules/${moduleId}/exceptions`, {
      rules: rules.map((rule) => ({ resource: rule.resource, action: rule.action, effect: rule.effect })),
    });
    setDraft(null);
    stored.reload();
    effective.reload();
  }

  const allowedCount = (effective.data ?? []).filter((entry) => entry.allowed).length;

  return (
    <Card
      title="Individual exceptions"
      hint={`Effectively allowed: ${allowedCount} of ${effective.data?.length ?? 0} operations`}
      actions={
        <div className="row">
          <select value={applicationId} onChange={(event) => setApplicationId(event.target.value)}>
            <option value="">Global roles only</option>
            {(applications.data ?? []).map((application) => (
              <option key={application.id} value={application.id}>
                through {application.name}
              </option>
            ))}
          </select>
          <select
            value={moduleId}
            onChange={(event) => {
              onModuleChange(event.target.value);
              setDraft(null);
            }}
          >
            {modules.map((module) => (
              <option key={module.id} value={module.id}>
                {module.name}
              </option>
            ))}
          </select>
          <button className="primary small" onClick={() => void save()} disabled={draft === null}>
            Save
          </button>
        </div>
      }
      tight
    >
      <div style={{ padding: '12px 16px 0' }}>
        <Notice tone="info">
          A tick here is an additional permission, a cross a revocation. The cross beats every permission coming
          from a role. The grey column shows what is left after all layers.
        </Notice>
      </div>
      <PermissionMatrix
        resources={resources.data ?? []}
        rules={rules}
        onChange={setDraft}
        effective={effective.data ?? []}
      />
    </Card>
  );
}

function AssignRoleDialog({
  userId,
  roles,
  onClose,
  onSaved,
}: {
  userId: string;
  roles: Role[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [roleId, setRoleId] = useState(roles[0]?.id ?? '');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    try {
      await api.post(`/api/admin/users/${userId}/roles`, {
        roleId,
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59Z`).toISOString() : undefined,
      });
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <Modal
      title="Assign role"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void save()} disabled={!roleId}>
            Assign
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Role">
        <select value={roleId} onChange={(event) => setRoleId(event.target.value)}>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Limited until" hint="leave empty for unlimited">
        <input type="date" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
      </Field>
    </Modal>
  );
}
